import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import languages from '../../helpers/utils/languages.js';
import useProject from '../../hooks/project/useProject';
import NoteIcon from '../utils/common/NoteIcon.jsx';
import TicketIcon from '../utils/common/TicketIcon.jsx';
import AddNoteDialog from './AddNoteDialog.jsx';
import NewTicketDialog from './NewTicketDialog.jsx';
import Notes from './Notes.jsx';
import ProjectAppBar from './ProjectAppBar.jsx';
import ProjectTickets from './ticket/ProjectTickets.jsx';
import '/node_modules/flag-icons/css/flag-icons.min.css';

const Project = () => {
  const profile = useSelector((state) => state?.login?.profile);
  const { projectId, ticketId, _taskId } = useParams();
  const [openNewTicketDialog, setOpenNewTicketDialog] = useState(false);
  const [newTicketDialogMessage, setNewTicketDialogMessage] = useState(null);
  const [mainContact, setMainContact] = useState(null);
  const [contentToDisplay, setContentToDisplay] = useState('tickets');
  const [displayAddNoteDialog, setDisplayAddNoteDialog] = useState(false);
  const [displayAddNoteDialogTicket, setDisplayAddNoteDialogTicket] =
    useState(false);
  const { project, isLoading, isValidating, mutate } = useProject(
    profile?._id,
    projectId
  );

  useEffect(() => {
    if (project?.contacts?.length > 0) {
      setMainContact(project?.contacts[0]);
    }
  }, [project]);

  const handleCreateNewTicket = (_e) => {
    setOpenNewTicketDialog(true);
  };

  const handleDisplayAddNoteDialog = (_event) => {
    setDisplayAddNoteDialog(!displayAddNoteDialog);
  };

  const handleDisplayAddNoteDialogTicket = (_event) => {
    setDisplayAddNoteDialogTicket(!displayAddNoteDialogTicket);
  };

  return isLoading ? (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress color="info" size={'4em'} />
    </Box>
  ) : (
    <>
      {project ? (
        <>
          <AddNoteDialog
            open={displayAddNoteDialog}
            handleCloseDialog={handleDisplayAddNoteDialog}
            project={project}
            ticket={project?.tickets?.find((t) => t._id === ticketId)}
            defaultType="USERNOTE"
            mutate={mutate}
          />
          <Grid container spacing={4}>
            <Grid>
              <ProjectAppBar project={project} mutate={mutate} />
            </Grid>
            <Grid size="grow">
              <Container maxWidth="full">
                <Box sx={{ flexGrow: 1 }}>
                  <AppBar
                    position="static"
                    sx={(theme) => ({
                      // Bordure en haut, plus épaisse
                      borderTop: `4px solid ${theme.palette.borders.main}`,
                      // Bordure en bas, plus épaisse
                      borderBottom: `4px solid ${theme.palette.borders.main}`,
                    })}

                    // sx={{
                    //   backgroundColor:
                    //     theme.palette.mode === "dark"
                    //       ? theme.palette.grey[900] // Fond noir en mode dark
                    //       : theme.palette.primary.light, // Fond en primary.light en mode light
                    //   // color: theme.palette.text.primary,
                    //   borderBottom: `4px solid ${theme.palette.text.primary}`, // Bordure visible
                    //   boxShadow: `0px 4px 10px ${
                    //     theme.palette.mode === "dark"
                    //       ? "rgba(255, 255, 255, 0.1)" // Ombre subtile en dark mode
                    //       : "rgba(0, 0, 0, 0.2)" // Ombre plus marquée en light mode
                    //   }`,
                    // }}
                  >
                    <Toolbar
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Box sx={{ display: 'flex' }}>
                        <Typography
                          component={'span'}
                          variant="h6"
                          sx={{ flexGrow: 1 }}
                        >
                          {mainContact?.firstName !== null &&
                          mainContact?.lastName !== null
                            ? `${mainContact?.firstName} ${mainContact?.lastName}`
                            : mainContact?.lastName !== null
                              ? `${mainContact?.lastName}`
                              : `${mainContact?.email}`}
                        </Typography>
                        <Tooltip
                          placement="top"
                          title={`${mainContact?.store?.country?.name}`}
                        >
                          <Typography
                            component={'span'}
                            sx={{ margin: 0.5, marginLeft: 1 }}
                          >
                            Pays
                            <span
                              className={`fi fi-${mainContact?.store?.country?.code2?.toLowerCase()}`}
                            ></span>
                          </Typography>
                        </Tooltip>
                        <Tooltip
                          placement="top"
                          title={`${
                            languages.find(
                              (language) =>
                                language.code2.toLowerCase() ===
                                mainContact?.language?.toLowerCase()
                            )?.lang
                          }`}
                        >
                          <Typography
                            component={'span'}
                            sx={{ margin: 0.5, marginLeft: 1 }}
                          >
                            Langue
                            <span
                              className={`fi fi-${
                                languages.find(
                                  (language) =>
                                    language.code2.toLowerCase() ===
                                    mainContact?.language?.toLowerCase()
                                )?.flag
                              }`}
                            ></span>
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Button
                          color="inherit"
                          onClick={() => {
                            setContentToDisplay('tickets');
                          }}
                          startIcon={<TicketIcon boxSize={24} iconSize={16} />}
                        >
                          Tickets
                        </Button>
                        <AddBoxOutlinedIcon
                          sx={{ marginTop: 0.7 }}
                          onClick={handleCreateNewTicket}
                        />
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ marginLeft: 1, marginRight: 1 }}
                        />
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ marginLeft: 1, marginRight: 1 }}
                        />
                        <Button
                          color="inherit"
                          onClick={() => {
                            setContentToDisplay('notes');
                          }}
                          startIcon={<NoteIcon boxSize={24} iconSize={16} />}
                        >
                          Notes
                        </Button>
                        <AddBoxOutlinedIcon
                          sx={{ marginTop: 0.7 }}
                          onClick={handleDisplayAddNoteDialog}
                        />
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {isValidating ? (
                          <CircularProgress color="info" size={'1.3em'} />
                        ) : (
                          <Box sx={{ width: '1.3em', height: '1.3em' }} />
                        )}
                      </Box>
                    </Toolbar>
                  </AppBar>
                </Box>

                {contentToDisplay === 'tickets' &&
                project?.tickets?.length > 0 ? (
                  <ProjectTickets
                    tickets={project?.tickets || null}
                    orders={project?.orders}
                    project={project}
                    currentTicket={project?.tickets?.find(
                      (t) => t._id === ticketId
                    )}
                    setOpenNewTicketDialog={setOpenNewTicketDialog}
                    setNewTicketDialogMessage={setNewTicketDialogMessage}
                    displayAddNoteDialog={displayAddNoteDialogTicket}
                    handleDisplayAddNoteDialog={
                      handleDisplayAddNoteDialogTicket
                    }
                    mutate={mutate}
                  />
                ) : contentToDisplay === 'tickets' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '80vh',
                    }}
                  >
                    <Typography sx={{ marginBottom: 2 }} color="info.main">
                      Pas de tickets sur ce projet actuellement...
                    </Typography>
                  </Box>
                ) : null}
                {contentToDisplay === 'notes' ? (
                  <>
                    <Notes
                      notes={project?.notes}
                      mutate={mutate}
                      project={project}
                      displayAddNoteDialog={displayAddNoteDialog}
                      handleDisplayAddNoteDialog={handleDisplayAddNoteDialog}
                      setContentToDisplay={setContentToDisplay}
                    />
                  </>
                ) : null}
                <NewTicketDialog
                  contacts={project?.contacts}
                  orders={project?.orders}
                  openNewTicketDialog={openNewTicketDialog}
                  setOpenNewTicketDialog={setOpenNewTicketDialog}
                  newTicketDialogMessage={newTicketDialogMessage}
                  setNewTicketDialogMessage={setNewTicketDialogMessage}
                  mutate={mutate}
                />
              </Container>
            </Grid>
          </Grid>
        </>
      ) : (
        <div>Pas de projet existant pour l&apos;id : {projectId}</div>
      )}
    </>
  );
};

export default Project;
